// React Component for the first step of the registration process
import { useContext, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

// @mui material components
import Checkbox from "@mui/material/Checkbox";
import { Grid, Icon, InputLabel } from "@mui/material";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// External libraries
import ReCAPTCHA from "react-google-recaptcha";

// Services
import authService from "services/auth-service";

// context
import { AuthContext } from "context";
import crudsService from "services/cruds-service";
import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";

// Step 1 of the registration process
const Step1 = ({
  // nextStepHandler,
  setAuth,
  auth,
  profileDetails,
  setProfileDetails,
}) => {
  const location = useLocation();
  const search = location.search || "";
  const params = new URLSearchParams(search);
  const redirectTo = params.get("redirect") || "/home-page"; // Default to home if no redirect
  const accesscode = params.get("accesscode") || "";
  const authContext = useContext(AuthContext);
  const recaptcha = useRef();
  const [captchaResolved, setCaptchaResolved] = useState(false);

  const [inputs, setInputs] = useState({
    name: "",
    email: "",
    password: "",
    organization: "",
    organization_role: "",
    accesscode: accesscode,
    agree: false,
  });
  const [errors, setErrors] = useState({
    nameError: false,
    emailError: false,
    passwordError: false,
    organizationError: false,
    organizationRoleError: false,
    agreeError: false,
    emailTaken: false,
    captchaError: false,
    accesscodeError: false,
    endError: "",
  });


  const changeHandler = (e) => {
    if (e.target.name === "agree") {
      setInputs({
        ...inputs,
        [e.target.name]: e.target.checked,
      });
    } else {
      setInputs({
        ...inputs,
        [e.target.name]: e.target.value,
      });
    }

    setErrors({
      ...errors,
      nameError: false,
      emailError: false,
      passwordError: false,
      organizationError: false,
      organizationRoleError: false,
      agreeError: false,
      emailTaken: false,
      captchaError: false,
      accesscodeError: false,
      endError: "",
    });
  };
  async function nextButtonHandler() {
    // helpers
    const trim = (s) => (s ?? "").trim();

    const validate = () => {
      const v = {
        captchaResolved,
        name: trim(inputs.name),
        email: trim(inputs.email).toLowerCase(),
        password: inputs.password ?? "",
        agree: !!inputs.agree,
        accesscode: trim(inputs.accesscode),
      };

      const errors = {};
      if (!v.captchaResolved) errors.captchaError = true;
      else if (!v.name) errors.nameError = true;
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email))
        errors.emailError = true;
      else if (!v.password || v.password.length < 8)
        errors.passwordError = true;
      else if (!v.agree) errors.agreeError = true;

      return { v, errors, ok: Object.keys(errors).length === 0 };
    };

    const setErr = (patch) => setErrors((prev) => ({ ...prev, ...patch }));

    const { v, errors: validationErrors, ok } = validate();
    if (!ok) {
      setErr(validationErrors);
      return;
    }

    // optional coupon gate (preserves your existing semantics)
    const hasAccessCode = !!v.accesscode;
    if (hasAccessCode) {
      try {
        await crudsService.checkCoupon(v.accesscode);
      } catch (e) {
        setErr({ accesscodeError: true });
        return;
      }
    }

    try {
      // verify email first (both paths do this)
      await authService.registerEmailVerification({ email: v.email });
    } catch (e) {
      // your original sets emailTaken (and in one branch also endError)
      setErr({ emailTaken: true, endError: e?.message });
      return;
    }

    // create account
    let response;
    try {
      response = await authService.registerUser({
        name: v.name,
        email: v.email,
        password: v.password,
        organization: inputs.organization,
        organization_role: inputs.organization_role,
      });
    } catch (e) {
      setErr({
        endError:
          e?.response?.data?.message ?? e?.message ?? "Registration failed",
      });
      return;
    }

    // post-register success
    try {
      analytics.track(events.REGISTRATION, { user_id: response.id });
    } catch {
      // don't fail the flow on analytics errors
    }

    setProfileDetails((prev) => ({
      ...prev,
      name: v.name,
      email: v.email,
      organization: inputs.organization,
      organization_role: inputs.organization_role,
      id: response.id,
    }));

    setAuth({
      id: response.id,
      access_token: response.access_token,
      refresh_token: response.refresh_token,
    });

    // If there is a coupon, apply it and only then login (your original behavior)
    if (hasAccessCode) {
      try {
        await crudsService.applyCouponCode({
          couponCode: v.accesscode,
          userId: response.id,
        });
        authContext.login(
          response.access_token,
          response.refresh_token,
          "/home-page",
        );
      } catch (e) {
        console.error("Error applying coupon:", e);
        setErr({ accesscodeError: true });
        return; // do not login on coupon failure (keeps original semantics)
      }
    } else {
      // no coupon: login directly to provided redirect
      authContext.login(
        response.access_token,
        response.refresh_token,
        redirectTo,
      );
    }
  }

  function resolveCaptcha(value) {
    setCaptchaResolved(true);
    setErrors({ ...errors, captchaError: false });
  }

  return (
    <MDBox pb={3} px={3}>
      <MDBox
        component="form"
        noValidate
        autoComplete="off"
        sx={{ maxWidth: 600, margin: "auto" }}
      >
        <Grid container spacing={2}>
          {/* Row 1: Name and Email */}
          <Grid item xs={12} sm={6}>
            <MDInput
              type="text"
              label="Name"
              variant="outlined"
              fullWidth
              name="name"
              value={inputs.name}
              onChange={changeHandler}
              error={errors.nameError}
              required
            />
            {errors.nameError && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                The name cannot be empty
              </MDTypography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              type="email"
              label="Email"
              variant="outlined"
              fullWidth
              name="email"
              value={inputs.email}
              onChange={changeHandler}
              error={errors.emailError}
              required
            />
            {errors.emailError && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                The email must be valid
              </MDTypography>
            )}
          </Grid>

          {/* Row 2: Password and Access Code */}
          <Grid item xs={12} sm={6}>
            <MDInput
              type="password"
              label="Password"
              variant="outlined"
              fullWidth
              name="password"
              value={inputs.password}
              onChange={changeHandler}
              error={errors.passwordError}
              required
            />
            {errors.passwordError && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                The password must be at least 8 characters
              </MDTypography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              type="text"
              label="Access Code"
              variant="outlined"
              fullWidth
              name="accesscode"
              value={inputs.accesscode}
              onChange={changeHandler}
              error={errors.accesscodeError}
            />
            {errors.accesscodeError && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                Invalid or Expired Access Code.
              </MDTypography>
            )}
          </Grid>

          {/* Row 3: Organization and Role */}
          <Grid item xs={12} sm={6}>
            <MDInput
              type="text"
              label="Organization"
              variant="outlined"
              fullWidth
              name="organization"
              value={inputs.organization}
              onChange={changeHandler}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              type="text"
              label="Role"
              variant="outlined"
              fullWidth
              name="organization_role"
              value={inputs.organization_role}
              onChange={changeHandler}
            />
          </Grid>
        </Grid>

        {/* Captcha and Agreement */}
        <MDBox mt={2} display="flex" flexDirection="column" alignItems="center">
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
            ref={recaptcha}
            onChange={resolveCaptcha}
          />
          {errors.captchaError && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              Please resolve the captcha
            </MDTypography>
          )}

          <MDBox display="flex" alignItems="center" mt={2}>
            <Checkbox name="agree" id="agree" onChange={changeHandler} />
            <InputLabel
              variant="standard"
              fontWeight="regular"
              color="text"
              sx={{ cursor: "pointer" }}
              htmlFor="agree"
            >
              &nbsp;&nbsp;I agree to the&nbsp;
            </InputLabel>
            <MDTypography
              component="a"
              href="https://www.quantuniversity.com/privacy.html"
              target="_blank"
              variant="button"
              fontWeight="bold"
              color="info"
              textGradient
            >
              Terms and Conditions.
            </MDTypography>
          </MDBox>
          {errors.agreeError && (
            <MDTypography variant="caption" color="error" fontWeight="light">
              You must agree to the Terms and Conditions
            </MDTypography>
          )}
        </MDBox>

        <MDBox mt={1} display="flex" justifyContent="center">
          <MDButton
            variant="gradient"
            color="info"
            style={{ width: "60%" }}
            onClick={nextButtonHandler}
          >
            Sign Up
          </MDButton>
        </MDBox>
        {/* Sign Up Button */}
        {errors.endError && (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <MDTypography
              variant="body2"
              color="error"
              fontWeight="light"
              display="flex"
              alignItems="center"
            >
              <Icon style={{ marginRight: "5px", fontSize: "1.2rem" }}>
                error_outlined
              </Icon>{" "}
              {errors.endError}
            </MDTypography>
          </MDBox>
        )}

        {/* Sign In Link */}
        <MDBox mt={2} textAlign="center">
          <MDTypography variant="button" color="text">
            Already have an account?{" "}
            <MDTypography
              component={Link}
              to={`/auth/login${search}`}
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Sign In
            </MDTypography>
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
};
export default Step1;
